import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import ServerData from '../../../common/serverData';
import { constants } from '../../../common/constants/constants';
import Table from '../../../common/table/TableFunctional';
import ArrowRight from '../../../images/arrow-right.png';
import Search from '../../../images/search.svg';
import BllinkUtils from '../../../common/utils/utils';
import { selectCustomStyles } from '../../../common/shared/select.styled';
import PrintPdfIcons from '../../../common/export_documents/PrintPdfIcons';
import { calcMonthsTextForSelectBox } from '../../../common/shared/statelessMethods';
import Money from '../../../common/utils/money';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { verifyLoggedIn } from '../../../store/auth/actions';
import { StyledTableWrapper } from '../monthly_reports_table/MonthlyReportsTable.styled';
import {StyledActionsBar} from '../report.styled';
import useBodyClass from '../../../common/hooks/useBodyClassName';

const ChequeReportForPartners = ({ match }) => {
    const { t } = useTranslation();
    const pdfContainer = useRef();
    const accessToken = useSelector((state) => state.auth.accessToken);
    let client = new ServerData(null, null, {accessToken: accessToken});
    const dispatch = useDispatch();
    const { partnerID, month, year } = match.params;

    const [months, setMonths] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState();
    const [filters, setFilters] = useState({});
    const [listData, setListData] = useState([]);
    const [title, setTitle] = useState('');
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const fetchIdRef = useRef(0);

    useBodyClass('report');

    const loadChequeData = async (start, end) => {
        dispatch(showLoader());

        if (!accessToken) {
            try {
                const { payload } = await dispatch(verifyLoggedIn());
                client = new ServerData(null, null, { accessToken: payload.accessToken });
            } catch (e) {
                return;
            }
        }

        const response = await client.get(`/managers/cashflow-partner/${partnerID}?month=${month}&year=${year}`, {
            start,
            end
        });

        let monthTotal = Money.roundNumber(response.value.Total);
        monthTotal = BllinkUtils.Strings.numberWithCommas(monthTotal);
        const currency = t(`currency.${constants.defaultCurrency}`);
        const tableTitle = t('reports.cheques_title', {monthName: t(`common.months.${month}`), year: year, amount: monthTotal, currency: currency});
        setTitle(tableTitle);
      
        dispatch(hideLoader());
        return response;
    };

    const fetchData = useCallback(async ({ pageSize, pageIndex }) => {
        const fetchId = ++fetchIdRef.current;
        if (fetchId === fetchIdRef.current) {
            const startRow = pageSize * pageIndex;
            const endRow = startRow + pageSize;

            const data = await loadChequeData(startRow, endRow);
            if (data) {
                setCurrentPage(pageIndex);
                const selectedMonths = data.value.paymentResults.filter(item => item.Method === constants.APIContract.paymentMethodsById[2].serverKey);
                setListData(selectedMonths);
                setPageCount(Math.ceil(selectedMonths / pageSize));
                getMonthsForDropdown(year);
            }          
        }
    }, []);

    const getMonthsForDropdown = year => {
        let monthsOptions = [];
        const years = [parseInt(year) - 1, year, parseInt(year) + 1];
        for (const year of years) {
            for (const monthInt of Object.keys(constants.MonthsToText)) {
                monthsOptions.push({
                    value: `${monthInt}_${year}`,
                    label: `${t(`common.months.${monthInt}`)} ${year}`
                });
            }
        }

        const selectedMonthFromOptions = monthsOptions.find(option => option.value === `${month}_${year}`);
        setSelectedMonth(selectedMonthFromOptions);
        setMonths(monthsOptions);
    };

    const parseAmount = amountString => {
        if (amountString) {
            return <span exceltext={amountString}>{BllinkUtils.Strings.numberWithCommas(amountString)}</span>
        }
    };

    const columns = [
        {
            Header: t('reports.apartmentName'),
            id: 'ApartmentNumber',
            accessor: 'ApartmentNumber',
        },
        {
            Header: t('reports.tenantName'),
            id: 'ClientName',
            accessor: 'ClientName',
        },
        {
            Header: t('reports.total_to_pay'),
            id: 'Amount',
            accessor: 'Amount',
            Cell: ({ row }) => {
                return <>{parseAmount(row.original.Amount)}</>
            }
        },
        {
            Header: t('reports.date_to_pay'),
            id: 'PaymentDate',
            accessor: 'PaymentDate',
            Cell: ({ row }) => {
                const dateParsed = BllinkUtils.BllinkTime.parseDate(row.original.PaymentDate);
                return <>{dateParsed}</>
            }
        },
        {
            Header: t('reports.ownership_type'),
            id: 'TenantType',
            accessor: 'TenantType',
            Cell: ({ row }) => {
                const tenantType = t(`reports.${row.original.TenantType}`);
                return <>{tenantType}</>
            }
        },
        {
            Header: t('reports.payment_for'),
            id: 'paymentFor',
            Cell: ({ row }) => {
                const dateParsed = row.original.months ? calcMonthsTextForSelectBox(row.original.months) : null;
                return <>{dateParsed}</>
            }
        },
        {
            Header: t('reports.payment_comment'),
            id: 'Comment',
            accessor: 'Comment',
        },
        {
            Header: t('payments.confirmation_id'),
            id: 'paymentID',
            accessor: 'paymentID',
        },
        {
            Header: t('payments.payment_method'),
            id: 'Method',
            accessor: 'Method',
            Cell: ({ row }) => {
                const orig = row.original;
                return <>{orig.Method && t(`buildings.${orig.Method}`)}</>
            }
        },     
    ];

    const onMonthChange = e => setSelectedMonth(e);

    useEffect(() => {
        if (selectedMonth && (selectedMonth.value.split('_')[0] !== month || selectedMonth.value.split('_')[1] !== year)) {            
            window.location.href = `/${constants.pages.monthlyPartnerChequesUrl}/${partnerID}/${selectedMonth.value.split('_')[0]}/${selectedMonth.value.split('_')[1]}`
        }
    }, [selectedMonth]);

    const onSearch = e => {
        setFilters({
            global: e.target.value
        });
    };

    return (
        <div>
            <div className="back-link mt-2" data-testid='back-link'>
                <Link to={`/${constants.pages.allBuildingsUrl}`}>
                    <img alt='return_to_details' src={ArrowRight} />
                    <span>{t('reports.go_back')}</span>
                </Link>
            </div>
            <StyledTableWrapper partner>
                <StyledActionsBar className={'actions-bar align-items-center'}>
                    <div className="control-with-icon search actions-bar-search">
                        <input
                            type="text"
                            className="form-control bllink-input-border"
                            name="search"
                            onChange={onSearch}
                        />
                        <img src={Search} alt={t('search.search')} />
                    </div>
                    <div className='actions-bar-select wide'>
                        <Select
                            styles={selectCustomStyles}
                            id="select-year"
                            onChange={onMonthChange}
                            options={months || []}
                            value={selectedMonth}
                        />
                    </div>

                    <PrintPdfIcons
                        classes={'ml-auto'}
                        pdfContainer={pdfContainer.current}
                        eventLabel={t('reports.cheques_export_title')}
                        titleString={t('reports.cheques_export_title')}
                        excelTitle={t('reports.cheques_export_title')}
                        reportName={t('reports.cheques_export_title')}
                    />
                </StyledActionsBar>

                {listData ? (
                    <div id="section-to-print" ref={pdfContainer}>
                        <Table
                            data={listData}
                            filters={filters}
                            tableTitle={title}
                            columns={columns}
                            exportTitle={t('reports.cheques_export_title')}
                            paginationOptions={{
                                fetchData: fetchData,
                                pageCount: pageCount,
                                currentPage: currentPage
                            }}
                        />
                    </div>
                ) : <div>{t('searchPayments.nothingFound')}</div>}
            </StyledTableWrapper>
        </div>
    );
};

export default ChequeReportForPartners;
